import { addMinutes, format, isBefore, isAfter, startOfDay, endOfDay, differenceInMinutes, parseISO } from 'date-fns';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, Timestamp, updateDoc, where, orderBy } from 'firebase/firestore';
import { 
  AlertCircle, 
  AlertTriangle, 
  ArrowDown, 
  ArrowRight, 
  Calendar as CalendarIcon, 
  CheckCircle2, 
  CheckSquare, 
  Clock, 
  Edit3, 
  FileText, 
  Loader2, 
  Minus, 
  Paperclip, 
  Plus, 
  ShieldAlert, 
  Square, 
  Trash2, 
  User, 
  Users, 
  Video, 
  X, 
  Info, 
  ChevronRight, 
  Layout, 
  ChevronLeft, 
  ExternalLink, 
  Lock,
  Library,
  RefreshCw
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { deleteGoogleEvent, GoogleAuthError, reconnectGoogle, syncLessonToGoogle, grantDrivePermission, clearGoogleSession } from '../lib/googleCalendar';
import { useTranslation } from '../lib/translations';
import { HomeworkAttachment, Lesson, Relationship, UserProfile, Program } from '../types';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { getFileIcon } from '../lib/fileUtils';
import SyncStatusBanner from './SyncStatusBanner';
import Tooltip from './Tooltip';
import ProgramPickerModal from './ProgramPickerModal';

// Added casting to any for import.meta to bypass environment variable type checking
const GOOGLE_DEVELOPER_KEY = (import.meta as any).env.VITE_GOOGLE_PICKER_API_KEY;
const GOOGLE_APP_ID = (import.meta as any).env.VITE_GOOGLE_APP_ID;

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: UserProfile | null;
  lessonId?: string;
  initialStudentId?: string;
  initialHomework?: string;
  initialAttachments?: HomeworkAttachment[];
  initialType?: 'lesson' | 'block';
  initialDate?: string;
  initialTime?: string;
  initialDuration?: number;
  googleEventId?: string;
  initialMeetLink?: string;
  readonly?: boolean;
}

const ScheduleModal: React.FC<ScheduleModalProps> = ({ 
  isOpen, 
  onClose, 
  profile, 
  lessonId,
  initialStudentId,
  initialHomework = '',
  initialAttachments = [],
  initialType = 'lesson',
  initialDate,
  initialTime,
  initialDuration = 60,
  googleEventId,
  initialMeetLink,
  readonly = false
}) => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [students, setStudents] = useState<Relationship[]>([]);
  const [allLessons, setAllLessons] = useState<Lesson[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  
  const currentSessionId = useRef<string | null>(null);
  const noteInputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const calendarRef = useRef<any>(null);
  
  const [type, setType] = useState<'lesson' | 'block'>(initialType);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [blockTitle, setBlockTitle] = useState('UNAVAILABLE');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [durationString, setDurationString] = useState('01:00');
  const [notes, setNotes] = useState<string[]>(['']);
  const [attachments, setAttachments] = useState<HomeworkAttachment[]>([]);
  const [createMeet, setCreateMeet] = useState(profile?.googleMeetEnabled ?? true);
  const [meetLink, setMeetLink] = useState('');
  const [calendarView, setCalendarView] = useState(lessonId ? 'timeGridDay' : 'dayGridMonth');
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [success, setSuccess] = useState(false);
  const [focusedNoteIndex, setFocusedNoteIndex] = useState<number | null>(null);
  const [showProgramPicker, setShowProgramPicker] = useState(false);
  const [needsGoogleReconnect, setNeedsGoogleReconnect] = useState(false);

  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');

  const [conflict, setConflict] = useState<Lesson | null>(null);
  const [overrideConflict, setOverrideConflict] = useState(false);

  const minutesToHHmm = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const hhmmToMinutes = (str: string) => {
    if (!str) return 60;
    const parts = str.split(':').map(Number);
    if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return 60;
    return (parts[0] * 60) + parts[1];
  };

  useEffect(() => {
    if (!isOpen) {
      currentSessionId.current = null;
      return;
    }

    const targetId = lessonId || 'new-session';
    if (currentSessionId.current === targetId) return;

    currentSessionId.current = targetId;
    setType(initialType);
    const initialNotesArr = initialHomework ? initialHomework.split('\n').filter(n => n.trim().length > 0) : [''];
    setNotes(initialNotesArr.length > 0 ? initialNotesArr : ['']);
    setAttachments(initialAttachments || []);
    
    const initialMins = initialDuration || 60;
    setDuration(initialMins);
    setDurationString(minutesToHHmm(initialMins));
    
    const effectiveDate = initialDate || format(new Date(), 'yyyy-MM-dd');
    const effectiveTime = initialTime || '14:00';
    
    setDate(effectiveDate);
    setTime(effectiveTime);
    
    const startObj = new Date(`${effectiveDate}T${effectiveTime}`);
    const endCalculated = addMinutes(isNaN(startObj.getTime()) ? new Date() : startObj, initialMins);
    
    setEndDate(format(endCalculated, 'yyyy-MM-dd'));
    setEndTime(format(endCalculated, 'HH:mm'));
    
    setSelectedStudentId(initialStudentId || '');
    setMeetLink(initialMeetLink || '');
    setCreateMeet(profile?.googleMeetEnabled ?? true);
    setShowConfirmDelete(false);
    setSuccess(false);
    setConflict(null);
    setOverrideConflict(false);
    setShowProgramPicker(false);
    setNeedsGoogleReconnect(false);

    const defaultView = lessonId ? 'timeGridDay' : 'dayGridMonth';
    setCalendarView(defaultView);

    fetchAllLessons();
    fetchPrograms();
  }, [isOpen, lessonId, initialType, initialHomework, initialAttachments, initialStudentId, initialDate, initialTime, initialDuration, initialMeetLink, profile?.googleMeetEnabled]);

  useEffect(() => {
    if (isOpen && calendarRef.current) {
      const api = calendarRef.current.getApi();
      setTimeout(() => {
        if (api.view.type !== calendarView) {
          api.changeView(calendarView);
        }
      }, 0);
    }
  }, [isOpen, calendarView]);

  const fetchAllLessons = async () => {
    if (!profile) return;
    try {
      const q = query(collection(db, 'lessons'), where('teacherId', '==', profile.uid), where('status', '==', 'scheduled'));
      const snap = await getDocs(q);
      setAllLessons(snap.docs.map(d => ({ id: d.id, ...d.data() } as Lesson)));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchPrograms = async () => {
    if (!profile) return;
    try {
      const q = query(collection(db, 'programs'), where('teacherId', '==', profile.uid));
      const snap = await getDocs(q);
      setPrograms(snap.docs.map(d => ({ id: d.id, ...d.data() } as Program)));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (type === 'block' && date && time && endDate && endTime) {
      const start = new Date(`${date}T${time}`);
      const end = new Date(`${endDate}T${endTime}`);
      if (isBefore(end, start)) {
        const correctedEnd = addMinutes(start, 30);
        setEndDate(format(correctedEnd, 'yyyy-MM-dd'));
        setEndTime(format(correctedEnd, 'HH:mm'));
      }
    }
  }, [date, time, endDate, endTime, type]);

  useEffect(() => {
    if (!isOpen || !date || !time || !profile) return;
    
    const check = async () => {
      setOverrideConflict(false);
      try {
        const startStr = `${date}T${time}`;
        const start = new Date(startStr);
        let end;
        if (type === 'block' && endDate && endTime) {
          end = new Date(`${endDate}T${endTime}`);
        } else {
          end = addMinutes(start, duration);
        }

        if (!readonly) {
          const overlap = allLessons.find(l => {
            if (l.id === lessonId) return false;
            const lStart = l.start.toDate();
            const lEnd = l.end.toDate();
            return (start < lEnd && end > lStart);
          });
          setConflict(overlap || null);
        }

        if (calendarRef.current && date) {
          const api = calendarRef.current.getApi();
          setTimeout(() => {
             api.gotoDate(date);
          }, 0);
        }
      } catch (e) {
        console.error("Calendar navigation or conflict check failed", e);
      }
    };

    const timer = setTimeout(check, 500);
    return () => clearTimeout(timer);
  }, [date, time, duration, endDate, endTime, type, profile, lessonId, isOpen, allLessons, readonly]);

  useEffect(() => {
    if (focusedNoteIndex !== null && noteInputsRef.current[focusedNoteIndex] && !readonly) {
      noteInputsRef.current[focusedNoteIndex]?.focus();
      setFocusedNoteIndex(null);
    }
  }, [focusedNoteIndex, readonly]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleAttachFiles = async (forceAccountSelect = false) => {
    if (readonly) return;
    
    const loadGapiPicker = () => {
      return new Promise<void>((resolve, reject) => {
        const gapi = (window as any).gapi;
        if (!gapi) {
          reject(new Error('GAPI script not loaded'));
          return;
        }
        gapi.load('picker', {
          callback: () => resolve(),
          onerror: () => reject(new Error('Failed to load picker library')),
          timeout: 10000,
          ontimeout: () => reject(new Error('GAPI picker load timeout'))
        });
      });
    };

    try {
      if (!(window as any).google?.picker) await loadGapiPicker();
      let token = sessionStorage.getItem('google_access_token');
      if (!token || forceAccountSelect) {
        token = await reconnectGoogle(forceAccountSelect);
      }
      
      if (!token) {
        alert(t.scheduleModal.authRequired);
        return;
      }

      if (!(window as any).google?.picker) {
        throw new Error('Google Picker namespace not available');
      }

      // Basic DocsView without chained methods that might fail
      const docsView = new (window as any).google.picker.DocsView((window as any).google.picker.ViewId.DOCS);

      const picker = new (window as any).google.picker.PickerBuilder()
        .addView(docsView)
        .setOAuthToken(token)
        .setDeveloperKey(GOOGLE_DEVELOPER_KEY)
        .setAppId(GOOGLE_APP_ID)
        .setOrigin(window.location.protocol + '//' + window.location.host)
        .setCallback((data: any) => {
          if (data.action === (window as any).google.picker.Action.PICKED) {
            setAttachments(prev => [...prev, ...data.docs.map((d: any) => ({
              name: d.name, mimeType: d.mimeType, webViewLink: d.url, driveId: d.id
            }))]);
          }
        })
        .build();
      picker.setVisible(true);
    } catch (err) {
      console.error('Drive picker error', err);
      if (window.confirm('Google Drive integration failed. This often happens if you are logged into multiple accounts or blocked by cookies. Would you like to re-select your account and try again?')) {
        clearGoogleSession();
        handleAttachFiles(true);
      }
    }
  };

  const removeAttachment = (id: string) => {
    if (readonly) return;
    setAttachments(prev => prev.filter(a => a.driveId !== id));
  };

  const handleApplyCurriculum = (newAttachments: HomeworkAttachment[]) => {
    const uniqueNew = newAttachments.filter(a => !attachments.find(att => att.driveId === a.driveId));
    setAttachments(prev => [...prev, ...uniqueNew]);
  };

  const fetchStudents = async () => {
    if (!profile) return;
    setLoading(true);
    try {
      const q = query(collection(db, 'relationships'), where('teacherId', '==', profile.uid), where('status', '==', 'active'));
      const snap = await getDocs(q);
      const enriched = await Promise.all(snap.docs.map(async d => {
        const data = d.data() as Relationship;
        const isPending = data.studentId.startsWith('pending:');
        let name = '';
        let email = '';
        if (isPending) {
          email = data.studentId.split(':')[1];
          name = email.split('@')[0];
        } else {
          const userDoc = await getDoc(doc(db, 'users', data.studentId));
          const userData = userDoc.data();
          name = userData?.displayName || 'Unknown';
          email = userData?.email || '';
        }
        return { ...data, id: d.id, studentName: name, studentEmail: email };
      }));
      setStudents(enriched);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen && profile?.role === 'teacher') fetchStudents();
  }, [isOpen, profile]);

  const handleDelete = async () => {
    if (!lessonId || !profile || readonly) return;
    setDeleting(true);
    try {
      if (profile.googleCalendarId && googleEventId) await deleteGoogleEvent(profile.googleCalendarId, googleEventId);
      await deleteDoc(doc(db, 'lessons', lessonId));
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose(); }, 1200);
    } catch (e) { console.error(e); }
    setDeleting(false);
  };

  const handleReconnectRequired = async () => {
    const token = await reconnectGoogle();
    if (token) {
      setNeedsGoogleReconnect(false);
      const dummyEvent = { preventDefault: () => {} } as React.FormEvent;
      handleSubmit(dummyEvent);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile || (type === 'lesson' && !selectedStudentId) || readonly) return;
    
    if (conflict && !overrideConflict) return;

    setSubmitting(true);
    setNeedsGoogleReconnect(false);
    
    try {
      const startDateTime = new Date(`${date}T${time}`);
      let endDateTime = type === 'block' ? new Date(`${endDate}T${endTime}`) : addMinutes(startDateTime, duration);
      const finalHomeworkString = notes.filter(n => n.trim().length > 0).join('\n');

      const lessonData: any = {
        teacherId: profile.uid,
        studentId: type === 'lesson' ? selectedStudentId : '',
        start: Timestamp.fromDate(startDateTime),
        end: Timestamp.fromDate(endDateTime),
        status: 'scheduled',
        notes: '',
        type,
        title: type === 'block' ? blockTitle : '',
        homework: { description: finalHomeworkString, attachments },
        createMeet,
        meetLink: meetLink || null
      };

      const selectedStudent = students.find(s => s.studentId === selectedStudentId);

      if (type === 'lesson' && selectedStudent?.studentEmail) {
        await Promise.all(attachments.map(a => grantDrivePermission(a.driveId, selectedStudent.studentEmail!)));
      }

      if (googleEventId) lessonData.googleEventId = googleEventId;

      let finalLessonId = lessonId;
      if (lessonId) {
        await updateDoc(doc(db, 'lessons', lessonId), lessonData);
      } else {
        const docRef = await addDoc(collection(db, 'lessons'), lessonData);
        finalLessonId = docRef.id;
      }
      
      if (profile.googleCalendarId) {
        try {
          const result = await syncLessonToGoogle({ ...lessonData, id: finalLessonId }, profile, selectedStudent);
          if (result.id === 'RECREATE_REQUESTED' || (result.id && result.id !== googleEventId)) {
            await updateDoc(doc(db, 'lessons', finalLessonId!), { 
              googleEventId: result.id === 'RECREATE_REQUESTED' ? null : result.id,
              meetLink: result.meetLink || null
            });
          } else if (result.meetLink) {
             await updateDoc(doc(db, 'lessons', finalLessonId!), { meetLink: result.meetLink });
          }
        } catch (syncErr) {
          if (syncErr instanceof GoogleAuthError && syncErr.status === 401) {
            setNeedsGoogleReconnect(true);
            setSubmitting(false);
            return; 
          }
          throw syncErr;
        }
      }

      setSuccess(true);
      setTimeout(() => { setSuccess(false); onClose(); }, 1200);
    } catch (e) { 
      alert(t.scheduleModal.deploymentFailed); 
    }
    setSubmitting(false);
  };

  const handleCalendarSelect = (selectInfo: any) => {
    if (readonly) return;
    if (selectInfo.view.type === 'dayGridMonth') {
      const api = calendarRef.current.getApi();
      api.changeView('timeGridDay', selectInfo.start);
      setCalendarView('timeGridDay');
      return;
    }

    setDate(format(selectInfo.start, 'yyyy-MM-dd'));
    setTime(format(selectInfo.start, 'HH:mm'));
    
    setEndDate(format(selectInfo.end, 'yyyy-MM-dd'));
    setEndTime(format(selectInfo.end, 'HH:mm'));

    const diff = differenceInMinutes(selectInfo.end, selectInfo.start);
    if (diff > 0) {
      setDuration(diff);
      setDurationString(minutesToHHmm(diff));
    }
  };

  const handleDurationStringChange = (val: string) => {
    if (readonly) return;
    setDurationString(val);
    const mins = hhmmToMinutes(val);
    setDuration(mins);
  };

  const calendarEvents = [
    ...allLessons.filter(l => l.id !== lessonId).map(l => {
      const isBlock = l.type === 'block';
      const rel = students.find(s => s.studentId === l.studentId);
      
      return {
        id: l.id,
        title: isBlock ? (l.title || 'UNAVAILABLE') : (rel?.studentName || 'Lesson'),
        start: l.start.toDate(),
        end: l.end.toDate(),
        backgroundColor: isBlock ? '#475569' : (rel?.color || '#cbd5e1'),
        borderColor: isBlock ? '#334155' : 'transparent',
        opacity: 0.4,
        classNames: ['text-[9px] font-black uppercase tracking-tighter'],
      };
    }),
    ...(date && time ? [{
        id: 'placeholder',
        title: type === 'block' ? (blockTitle || 'Block Placeholder') : (students.find(s => s.studentId === selectedStudentId)?.studentName || 'Lesson'),
        start: new Date(`${date}T${time}`),
        end: type === 'block' && endDate && endTime ? new Date(`${endDate}T${endTime}`) : addMinutes(new Date(`${date}T${time}`), duration),
        backgroundColor: type === 'block' ? '#1e293b' : (students.find(s => s.studentId === selectedStudentId)?.color || '#9333ea'),
        borderColor: '#ffffff',
        opacity: readonly ? 0.7 : 1,
        classNames: [`text-[10px] font-black uppercase tracking-widest shadow-2xl ring-2 ring-white/50 border-2 ${readonly ? 'grayscale-[0.5]' : ''}`],
    }] : [])
  ];

  const selectedStudent = students.find(s => s.studentId === selectedStudentId);
  const creditsRemainingValue = selectedStudent ? selectedStudent.lessonsPaid - selectedStudent.lessonsCompleted : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-300 !mt-0">
      <div className="bg-white dark:bg-slate-900 w-full max-w-[1440px] h-[90vh] rounded-2xl border border-slate-200 dark:border-white/5 shadow-2xl relative overflow-hidden flex flex-col lg:flex-row animate-in zoom-in duration-500">
        
        <div className="hidden lg:flex flex-col w-[45%] border-r border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-[#03081a] p-6">
          <div className="mb-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                  <CalendarIcon className="text-purple-600" size={20} /> {t.scheduleModal.availabilityView}
                </h3>
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mt-1">{t.scheduleModal.availabilitySub}</p>
              </div>
              
              <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg border border-slate-200 dark:border-slate-700">
                {[
                  { id: 'timeGridDay', label: t.common.day },
                  { id: 'timeGridWeek', label: t.common.week },
                  { id: 'dayGridMonth', label: t.common.month }
                ].map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setCalendarView(v.id)}
                    type="button"
                    className={`px-3 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${
                      calendarView === v.id 
                      ? 'bg-white dark:bg-slate-700 text-purple-600 shadow-sm' 
                      : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-hidden rounded-xl border border-slate-200 dark:border-white/5 bg-white dark:bg-slate-900 p-2 shadow-inner availability-calendar-compact">
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={calendarView}
              headerToolbar={{
                left: 'prev,next',
                center: 'title',
                right: ''
              }}
              events={calendarEvents}
              height="100%"
              selectable={!readonly}
              selectMirror={!readonly}
              select={handleCalendarSelect}
              slotMinTime="08:00:00"
              slotMaxTime="22:00:00"
              nowIndicator={true}
              allDaySlot={false}
              locale={language}
              navLinks={true}
              navLinkDayClick={(date) => {
                const api = calendarRef.current.getApi();
                api.changeView('timeGridDay', date);
                setCalendarView('timeGridDay');
              }}
              datesSet={(arg) => {
                if (arg.view.type !== calendarView) {
                  setCalendarView(arg.view.type);
                }
              }}
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col h-full bg-white dark:bg-slate-900 overflow-y-auto overflow-x-hidden custom-scrollbar p-6 sm:p-10 relative">
          <button onClick={onClose} type="button" className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-all active:scale-90 z-20">
            <X size={24} />
          </button>

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${type === 'lesson' ? 'bg-purple-600 text-white' : 'bg-slate-900 dark:bg-slate-700 text-white'}`}>
                {type === 'lesson' ? <CalendarIcon size={20} /> : <ShieldAlert size={20} />}
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter">
                    {lessonId ? (type === 'lesson' ? (readonly ? t.common.completed + ' ' + t.scheduleModal.performance : t.scheduleModal.editPerformance) : t.scheduleModal.editBlock) : type === 'lesson' ? t.scheduleModal.scheduleSession : t.scheduleModal.blockAvailability}
                  </h2>
                  {readonly && (
                    <span className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 border border-slate-200 dark:border-white/5 text-[8px] font-black uppercase tracking-widest">
                      <Lock size={10} /> {t.studentDetail.closeRecord.toUpperCase()}
                    </span>
                  )}
                </div>
                <p className="text-slate-500 text-sm font-medium leading-relaxed">
                  {readonly ? t.studentDetail.closeRecord : lessonId ? t.scheduleModal.editDesc : type === 'lesson' ? t.scheduleModal.scheduleDesc : t.scheduleModal.blockDesc}
                </p>
              </div>
            </div>
            
            {!readonly && (
              <SyncStatusBanner profile={profile} onReconnectSuccess={fetchAllLessons} />
            )}
          </div>

          {success ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 animate-in zoom-in duration-300">
              <div className="w-20 h-20 bg-teal-500/10 text-teal-600 rounded-xl flex items-center justify-center mb-4">
                <CheckCircle2 size={48} strokeWidth={2.5} />
              </div>
              <h3 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">{t.common.success}</h3>
              <p className="text-slate-500 text-sm font-medium">{t.common.done}</p>
            </div>
          ) : showConfirmDelete ? (
            <div className="py-10 text-center space-y-6 animate-in zoom-in duration-200">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 text-red-600 rounded-xl flex items-center justify-center mx-auto mb-6">
                <AlertTriangle size={32} />
              </div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white">{t.scheduleModal.confirmRemoval}</h3>
              <div className="flex flex-col gap-3">
                <button onClick={handleDelete} type="button" disabled={deleting} className="w-full py-4 bg-red-600 text-white rounded-lg font-black text-[11px] uppercase tracking-widest shadow-xl shadow-red-500/20">{deleting ? <Loader2 className="animate-spin" size={18} /> : t.scheduleModal.deletePermanently}</button>
                <button onClick={() => setShowConfirmDelete(false)} type="button" className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 rounded-lg font-black text-[11px] uppercase tracking-widest">{t.scheduleModal.keepSession}</button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8 pb-10">
              {needsGoogleReconnect && (
                <div className="p-5 rounded-xl border-2 border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 flex flex-col sm:flex-row items-center justify-between gap-4 animate-in slide-in-from-top-4 duration-300">
                  <div className="flex items-center gap-3 text-center sm:text-left">
                    <AlertTriangle size={24} className="shrink-0" />
                    <p className="text-xs font-black uppercase tracking-widest leading-relaxed">
                      {t.profile.connectionExpired}. <br />
                      <span className="opacity-70 normal-case font-bold">Please reconnect to sync this session to Google Calendar.</span>
                    </p>
                  </div>
                  <button 
                    type="button" 
                    onClick={handleReconnectRequired}
                    className="flex items-center gap-2 px-6 py-2.5 bg-amber-600 text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-amber-700 shadow-lg shadow-amber-500/20 transition-all active:scale-95 disabled:opacity-50"
                  >
                    <RefreshCw size={14} /> {t.profile.reconnect}
                  </button>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-8">
                {type === 'lesson' ? (
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.scheduleModal.assignedStudent}</label>
                    <div className="flex gap-2">
                      <select required value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)} disabled={!!initialStudentId || !!lessonId || readonly} className="flex-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-black focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 outline-none disabled:opacity-50 appearance-none cursor-pointer">
                        <option value="" disabled>{t.scheduleModal.selectStudent}</option>
                        {students.map(s => <option key={s.studentId} value={s.studentId}>{s.studentName}</option>)}
                      </select>
                      {selectedStudentId && (
                        <button
                          type="button"
                          onClick={() => { onClose(); navigate(`/student/${selectedStudentId}`); }}
                          className="p-3 text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-all shrink-0 flex items-center justify-center"
                          title="View Student Profile"
                        >
                          <ExternalLink size={20} />
                        </button>
                      )}
                    </div>
                    {creditsRemainingValue !== null && (
                      <p className={`text-[10px] font-black uppercase mt-1 ml-1 tracking-widest animate-in slide-in-from-top-1 ${creditsRemainingValue <= 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {t.scheduleModal.creditsRemaining.replace('{count}', creditsRemainingValue.toString())}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.scheduleModal.blockTitle}</label>
                    <input required type="text" value={blockTitle} onChange={(e) => setBlockTitle(e.target.value)} disabled={readonly} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-black focus:ring-4 focus:ring-slate-500/10 outline-none disabled:opacity-50" />
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.scheduleModal.dateTime} (Start)</label>
                  <div className="flex gap-2">
                    <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} disabled={readonly} className="flex-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-black outline-none focus:ring-4 focus:ring-purple-500/10 disabled:opacity-50" />
                    <input type="time" required value={time} onChange={(e) => setTime(e.target.value)} disabled={readonly} className="w-32 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-black outline-none focus:ring-4 focus:ring-purple-500/10 disabled:opacity-50" />
                  </div>
                </div>
              </div>

              {conflict && !readonly && (
                <div className="w-full p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 rounded-xl animate-in slide-in-from-top-2">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-amber-600 mt-0.5" size={18} />
                    <div className="flex-1">
                      <p className="text-xs font-black uppercase tracking-widest text-amber-700 dark:text-amber-400 mb-1">{t.scheduleModal.conflictDetected}</p>
                      <p className="text-[11px] font-medium text-amber-600 dark:text-amber-500/80 leading-relaxed mb-3">
                        {t.scheduleModal.conflictWith.replace('{name}', conflict.type === 'block' ? (conflict.title || 'Blocked Time') : (students.find(s => s.studentId === conflict.studentId)?.studentName || 'Another Lesson'))}
                        {' '} ({format(conflict.start.toDate(), 'p')} - {format(conflict.end.toDate(), 'p')}).
                      </p>
                      <button 
                        type="button" 
                        onClick={() => setOverrideConflict(!overrideConflict)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-[10px] font-black uppercase tracking-widest transition-all ${overrideConflict ? 'bg-amber-600 text-white border-transparent' : 'bg-white dark:bg-slate-800 text-amber-600 border-amber-200 dark:border-amber-500/30'}`}
                      >
                        {overrideConflict ? <CheckSquare size={14} /> : <Square size={14} />} 
                        {t.scheduleModal.ignoreConflict}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-8 pt-4 border-t border-slate-100 dark:border-white/5">
                {type === 'lesson' ? (
                  <>
                    <div className="space-y-3">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.scheduleModal.durationStrategy}</label>
                      <div className="relative">
                        <input 
                          type="time" 
                          value={durationString} 
                          onChange={(e) => handleDurationStringChange(e.target.value)}
                          disabled={readonly}
                          className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-black outline-none focus:ring-4 focus:ring-purple-500/10 appearance-none disabled:opacity-50" 
                        />
                        <Clock className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Conferencing</label>
                      {!meetLink && !readonly && (
                        <button type="button" onClick={() => setCreateMeet(!createMeet)} className={`flex items-center gap-3 p-3 w-full rounded-lg border transition-all ${createMeet ? 'bg-purple-50 dark:bg-purple-500/5 border-purple-200 dark:border-purple-500/20 text-purple-700 dark:text-purple-300' : 'bg-slate-50 dark:bg-slate-950 border-slate-100 dark:border-white/5 text-slate-500'}`}>
                          {createMeet ? <CheckSquare size={18} /> : <Square size={18} />} <div className="text-xs font-bold"><Video size={14} className="inline mr-2" /> Create Google Meet</div>
                        </button>
                      )}
                      {meetLink && (
                        <a 
                          href={meetLink} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className={`p-3 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg ${readonly ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 grayscale pointer-events-none opacity-60' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-500/20'}`}
                        >
                          <Video size={14} /> {t.dashboard.joinMeet}
                        </a>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.scheduleModal.dateTime} (End)</label>
                    <div className="flex gap-2">
                      <input type="date" required value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={readonly} className="flex-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-black outline-none focus:ring-4 focus:ring-purple-500/10 disabled:opacity-50" />
                      <input type="time" required value={endTime} onChange={(e) => setEndTime(e.target.value)} disabled={readonly} className="w-32 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 px-4 text-sm font-black outline-none focus:ring-4 focus:ring-purple-500/10 disabled:opacity-50" />
                    </div>
                  </div>
                )}
              </div>

              {type === 'lesson' && (
                <div className="space-y-6 pt-4 border-t border-slate-100 dark:border-white/5">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-2"><FileText size={14} className="text-purple-600" /> {t.scheduleModal.sessionNotes}</label>
                      {!readonly && (
                        <button type="button" onClick={() => { setNotes([...notes, '']); setFocusedNoteIndex(notes.length); }} className="text-[9px] font-black text-purple-600 uppercase tracking-widest flex items-center gap-1"><Plus size={12} /> {t.scheduleModal.addNote}</button>
                      )}
                    </div>
                    {notes.map((note, idx) => (
                      <div key={idx} className="flex gap-2 animate-in slide-in-from-left-2 duration-300">
                        <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-purple-600 shrink-0" />
                        <input ref={el => { noteInputsRef.current[idx] = el; }} type="text" value={note} onChange={(e) => { const n = [...notes]; n[idx] = e.target.value; setNotes(n); }} disabled={readonly} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const n = [...notes]; n.splice(idx + 1, 0, ''); setNotes(n); setFocusedNoteIndex(idx + 1); } }} className="flex-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-2 px-3 text-sm font-medium outline-none focus:ring-2 focus:ring-purple-500/10 disabled:opacity-50" />
                        {!readonly && (
                          <button type="button" onClick={() => setNotes(notes.filter((_, i) => i !== idx))} className="p-2 text-slate-400 hover:text-red-500"><Minus size={14} /></button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                       <div className="flex items-center gap-4">
                        {!readonly && (
                          <button type="button" onClick={() => handleAttachFiles()} className="flex items-center gap-2 text-[9px] font-black text-purple-600 uppercase tracking-widest hover:underline transition-all"><Paperclip size={14} /> {t.scheduleModal.linkDrive}</button>
                        )}
                        {!readonly && programs.length > 0 && (
                          <button 
                            type="button" 
                            onClick={() => setShowProgramPicker(true)} 
                            className="flex items-center gap-2 text-[9px] font-black text-indigo-600 uppercase tracking-widest hover:underline"
                          >
                            <Library size={14} /> {t.programs.bulkAdd}
                          </button>
                        )}
                       </div>
                    </div>

                    <div className="grid gap-2">
                      {attachments.map((file) => (
                        <div key={file.driveId} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-white/5 rounded-lg text-[11px] font-bold group">
                          <div className="flex items-center gap-3 truncate flex-1">
                            {getFileIcon(file.mimeType)}
                            <a href={file.webViewLink} target="_blank" rel="noopener noreferrer" className="truncate hover:text-purple-600 transition-colors">{file.name}</a>
                          </div>
                          {!readonly && (
                            <button type="button" onClick={() => removeAttachment(file.driveId)} className="text-slate-400 hover:text-red-500 pl-2 transition-colors"><Trash2 size={14} /></button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4 sticky bottom-0 pt-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-white/5 mt-auto">
                {readonly ? (
                  <button 
                    type="button" 
                    onClick={onClose} 
                    className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-lg font-black text-[11px] uppercase tracking-widest hover:bg-slate-200 dark:hover:bg-slate-700 transition-all active:scale-95 shadow-sm"
                  >
                    {t.studentDetail.closeRecord}
                  </button>
                ) : (
                  <>
                    {lessonId && (
                      <button type="button" onClick={() => setShowConfirmDelete(true)} className="p-4 bg-red-50 dark:bg-red-900/10 text-red-600 rounded-lg transition-colors hover:bg-red-100 dark:hover:bg-red-900/20"><Trash2 size={20} /></button>
                    )}
                    <button 
                      type="submit" 
                      disabled={submitting || (conflict !== null && !overrideConflict)} 
                      className={`flex-1 py-4 rounded-lg font-black text-[11px] uppercase tracking-widest shadow-xl transition-all active:scale-95 ${(conflict !== null && !overrideConflict) ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700 shadow-purple-500/20'} disabled:opacity-50`}
                    >
                      {submitting ? <Loader2 className="animate-spin mx-auto" size={24} /> : lessonId ? t.scheduleModal.updateSession : type === 'lesson' ? t.scheduleModal.confirmPerformance : t.scheduleModal.blockStudio}
                    </button>
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      <ProgramPickerModal 
        isOpen={showProgramPicker} 
        onClose={() => setShowProgramPicker(false)} 
        programs={programs} 
        onApply={handleApplyCurriculum} 
      />
    </div>
  );
};

export default ScheduleModal;
